import React, { useMemo } from 'react';
import {
  MintTimeStatusEnum,
  ReleaseAlbumRevealFilterOptionEnum,
} from '@soundxyz/utils/constants/enums';
import { ReleaseInfoCard, SongCardSkeleton } from '@components/SongCards/ReleaseInfoCard';
import { GridCarousel } from '@components/main-components/carousel/GridCarousel';
import { Chip } from '@components/main-components/elements/chips/Chip';
import { useAuth } from '@context/auth/base';
import { triggerLogin } from '@context/auth/store';
import type { ReleasesCursorFilterArgs } from '@graphql/reactQuery';
import { updateRecentPageStatus } from '@hooks/ReleaseStatus/useReleaseStatus';
import { useRecentDrops } from '@hooks/useRecentDrops';
import { NAV_LINKS } from '@utils/constants';
import { getNextTrackIds } from '@utils/helpers';
import { EmptyState } from '../FeaturedStatus/EmptyState';
import { FeaturedStatusEnum } from '../FeaturedStatus/schema';
import { useFeaturedStatus } from '../FeaturedStatus/useFeaturedStatus';
import { LandingHeading } from '../shared/LandingHeading';

const SECTION_LIMIT = 10;
export const RecentDropsSection = () => {
  const { isAuthenticatedWithAPI, authUser } = useAuth();

  const { recent, updateRecent } = useFeaturedStatus();

  const filter: ReleasesCursorFilterArgs = useMemo(() => {
    const artistFollowedByUser =
      recent === FeaturedStatusEnum.Enum.FOLLOWING && authUser ? authUser.id : undefined;

    return {
      mintTimeStatus: [MintTimeStatusEnum.PAST],
      releaseAlbumRevealStatus: ReleaseAlbumRevealFilterOptionEnum.ONLY_NOT_REVEALED_ALBUMS,
      artistFollowedByUser,
      featuredStatus: recent === FeaturedStatusEnum.Enum.HOT ? recent : 'ALL',
    };
  }, [authUser, recent]);

  const { recentDrops, loadMore, isInitialLoading, isError } = useRecentDrops({
    limit: SECTION_LIMIT,
    filter,
  });

  const skeletonSlides = Array.from({ length: 5 }).map((_, i) => ({
    id: i.toString(),
    slide: <SongCardSkeleton key={`freshly-minted-skeleton-${i}`} />,
  }));

  const releaseSlides = useMemo(
    () =>
      recentDrops.map(release => {
        return {
          id: release.id,
          slide: (
            <ReleaseInfoCard
              key={release.id}
              data={release}
              getNextTrackIds={getNextTrackIds({
                trackId: release.track.id,
                releases: recentDrops,
              })}
            />
          ),
        };
      }),
    [recentDrops],
  );

  /**
   * When we resize from mobile to desktop
   * tis makes sure the grid always only shows 8 items
   */
  const recentDropsGrid = useMemo(() => recentDrops.slice(0, SECTION_LIMIT), [recentDrops]);

  return (
    <div className="flex flex-col px-4 md:px-6">
      <LandingHeading
        header="Recent Drops"
        pathnameUri={NAV_LINKS.Recent}
        onHeaderClick={() => {
          updateRecentPageStatus({
            featuredStatus: recent,
          });
        }}
      />
      <div className="mb-2 flex items-center gap-2 sm:mb-4">
        <Chip
          text="Hot"
          isSelected={recent === FeaturedStatusEnum.Enum.HOT}
          onClick={() => updateRecent(FeaturedStatusEnum.Enum.HOT)}
        />
        <Chip
          text="Following"
          isSelected={recent === FeaturedStatusEnum.Enum.FOLLOWING}
          onClick={() => {
            if (!isAuthenticatedWithAPI) {
              triggerLogin();
              return;
            }

            updateRecent(FeaturedStatusEnum.Enum.FOLLOWING);
          }}
        />
      </div>

      <div className="block">
        {isInitialLoading ? (
          <GridCarousel
            slides={skeletonSlides}
            onEndReached={null}
            onInternalActiveIndexChange={null}
            columnAmount={null}
            hideArrows={false}
            externalActiveIndex={null}
            spacing={null}
            alignSlidesLeft={false}
            ssrSlideWidth={undefined}
            columnType="default"
          />
        ) : !recentDropsGrid.length || isError ? (
          <EmptyState type="recent" status={recent} />
        ) : (
          <GridCarousel
            slides={releaseSlides}
            onEndReached={loadMore}
            onInternalActiveIndexChange={null}
            columnAmount={null}
            hideArrows={false}
            externalActiveIndex={null}
            spacing={null}
            alignSlidesLeft={false}
            ssrSlideWidth={undefined}
            columnType="default"
          />
        )}
      </div>
    </div>
  );
};
